/**
 * Lesson player (Sprint 2 step 6).
 *
 * Route: ``/lesson/:moduleSlug/:lessonSlug``. Phase 0 hardcodes the
 * course slug to ``cait-foundations`` (only course in flight); a later
 * sprint that adds a second course will swap this for an enrollment
 * lookup.
 *
 * Composition:
 *   - Header: module / lesson title, estimated minutes, learning
 *     objectives.
 *   - Article body: placeholder for now. Full MDX rendering via
 *     ``@mdx-js/rollup`` bundling is a Sprint 2 follow-up — the
 *     content team is in parallel and only one lesson is authored.
 *     The placeholder still scrolls (long enough to satisfy the
 *     scroll-coverage heartbeat), so the rest of the flow is
 *     end-to-end testable.
 *   - Heartbeat: ``useLessonHeartbeat`` tracks scroll progress of
 *     the article body element and POSTs to the server with throttling.
 *   - Knowledge check: `<KnowledgeCheck>` (idle → attempting → reviewing).
 *   - Completion: progress meters, "Mark complete" button gated on
 *     ``completion-status.blocking_reasons === []``. On complete →
 *     navigate to ``next_lesson_id``.
 *
 * Anti-cheat: completion is server-validated. Clicking "Mark complete"
 * before thresholds are met returns 422 with the reasons — surfaced
 * inline.
 */
import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { KnowledgeCheck } from "@/components/KnowledgeCheck";
import { LessonBody } from "@/components/LessonBody";
import { StudyGuidePlayer } from "@/components/StudyGuidePlayer";
import { useLessonHeartbeat } from "@/hooks/useLessonHeartbeat";
import { API_BASE_URL, ApiError } from "@/lib/api";
import {
  completeLesson,
  getCompletionStatus,
  getLessonBySlug,
  type LessonCompletionStatus,
  type LessonDetail,
} from "@/lib/lesson-api";

const COURSE_SLUG = "cait-foundations";

type LoadState =
  | { kind: "loading" }
  | { kind: "error"; status?: number; message: string }
  | {
      kind: "locked";
      blockingMessage: string;
      blockingModuleSequence?: number;
    }
  | {
      kind: "ready";
      lesson: LessonDetail;
      completion: LessonCompletionStatus;
    };

/**
 * Map server blocking-reason tokens to plain-English copy a candidate
 * can act on. Server emits e.g. ``scroll_coverage_below_threshold: 12% < 80%``
 * — we extract the percentages and rewrite the prefix.
 */
function humanizeBlockingReason(token: string): string {
  const scrollMatch = token.match(/^scroll_coverage_below_threshold:\s*(\d+)%\s*<\s*(\d+)%/);
  if (scrollMatch) {
    const [, have, need] = scrollMatch;
    return `Read more of the lesson — ${have}% scrolled, ${need}% needed to mark complete.`;
  }
  const videoMatch = token.match(/^video_coverage_below_threshold:\s*(\d+)%\s*<\s*(\d+)%/);
  if (videoMatch) {
    const [, have, need] = videoMatch;
    return `Watch more of the video — ${have}% played, ${need}% needed.`;
  }
  if (token === "knowledge_check_not_passed") {
    return "Pass the knowledge check (4 of 5 correct).";
  }
  // Unknown token — show the raw string but stripped of the technical
  // colon prefix so it doesn't look like a debug dump.
  return token.replace(/_/g, " ");
}

function redirectToLogin(returnTo: string): void {
  // Prepend API_BASE_URL so the redirect crosses to the backend origin
  // (the SPA's same-origin has no /api/auth/login route — it'd render
  // blank). Mirrors the same fix in CourseHome.
  window.location.href = `${API_BASE_URL}/api/auth/login?return_to=${encodeURIComponent(returnTo)}`;
}

export interface LessonPlayerProps {
  /** Test seam: swap the redirect for a spy. */
  onUnauthenticated?: (returnTo: string) => void;
}

export function LessonPlayer({
  onUnauthenticated = redirectToLogin,
}: LessonPlayerProps = {}): React.JSX.Element {
  const params = useParams<{ moduleSlug: string; lessonSlug: string }>();
  const moduleSlug = params.moduleSlug ?? "";
  const lessonSlug = params.lessonSlug ?? "";
  const navigate = useNavigate();
  const articleRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [completing, setCompleting] = useState(false);
  const [completeError, setCompleteError] = useState<string | null>(null);

  // Heartbeat hook — driven by the article body element and (when
  // present) the lesson video element.
  const heartbeat = useLessonHeartbeat({
    lessonId: state.kind === "ready" ? state.lesson.id : "",
    scrollTarget: state.kind === "ready" ? articleRef.current : null,
    videoTarget: state.kind === "ready" ? videoRef.current : null,
  });

  // Re-fetch completion status whenever heartbeat reports new pcts or KC
  // passes — these are the events that move the readiness needle.
  const refreshCompletion = useCallback(async (): Promise<void> => {
    if (state.kind !== "ready") return;
    try {
      const fresh = await getCompletionStatus(state.lesson.id);
      setState({ kind: "ready", lesson: state.lesson, completion: fresh });
    } catch {
      // Best-effort polling — surface only on the explicit Complete click.
    }
  }, [state]);

  // Initial load.
  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    void (async () => {
      try {
        const lesson = await getLessonBySlug(COURSE_SLUG, moduleSlug, lessonSlug);
        if (cancelled) return;
        const completion = await getCompletionStatus(lesson.id);
        if (cancelled) return;
        setState({ kind: "ready", lesson, completion });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          onUnauthenticated(`/lesson/${moduleSlug}/${lessonSlug}`);
          return;
        }
        // DEC-0013: 403 with detail.blocking === "module_quiz_not_passed"
        // is the module-unlock gate. FastAPI wraps the structured detail
        // as ``{"detail": {...}}`` so we unwrap once before reading.
        if (err instanceof ApiError && err.status === 403) {
          const envelope = err.body as { detail?: unknown } | null;
          const detail = envelope?.detail as
            | { blocking?: string; blocking_module_sequence?: number; message?: string }
            | undefined;
          if (detail && detail.blocking === "module_quiz_not_passed") {
            const locked: LoadState =
              detail.blocking_module_sequence !== undefined
                ? {
                    kind: "locked",
                    blockingMessage:
                      detail.message ||
                      `Module ${detail.blocking_module_sequence} quiz not yet passed.`,
                    blockingModuleSequence: detail.blocking_module_sequence,
                  }
                : {
                    kind: "locked",
                    blockingMessage:
                      detail.message || "Pass the prior module's quiz first.",
                  };
            setState(locked);
            return;
          }
        }
        const message =
          err instanceof Error ? err.message : "Failed to load lesson.";
        const errState: LoadState =
          err instanceof ApiError
            ? { kind: "error", status: err.status, message }
            : { kind: "error", message };
        setState(errState);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [moduleSlug, lessonSlug, onUnauthenticated]);

  // When heartbeat reports new scroll_pct that crosses 80, refresh status.
  useEffect(() => {
    if (state.kind !== "ready") return;
    if (
      heartbeat.scrollPct >= state.completion.completion_threshold &&
      state.completion.scroll_pct < state.completion.completion_threshold
    ) {
      void refreshCompletion();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heartbeat.scrollPct]);

  const onMarkComplete = useCallback(async (): Promise<void> => {
    if (state.kind !== "ready") return;
    setCompleting(true);
    setCompleteError(null);
    try {
      const updated = await completeLesson(state.lesson.id);
      setState({ kind: "ready", lesson: state.lesson, completion: updated });
      if (updated.next_lesson_slug_path) {
        // Detect module boundary crossing. The backend's
        // next_lesson_slug_path is the next-lesson-in-sequence
        // regardless of module gating, so for the LAST lesson of
        // module N it returns module N+1's L1 — which the user
        // can't enter until they pass module N's quiz. Route them
        // to the quiz instead so they don't land on a locked page.
        // (Path shape: /lesson/{moduleSlug}/{lessonSlug}.)
        const nextModule = updated.next_lesson_slug_path.match(
          /^\/lesson\/([^/]+)\//,
        )?.[1];
        if (nextModule && nextModule !== moduleSlug) {
          void navigate(`/module/${moduleSlug}/quiz`);
        } else {
          // Same module — direct deep-link to the next lesson.
          void navigate(updated.next_lesson_slug_path);
        }
      } else {
        // No next lesson — back to course home.
        void navigate("/course");
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        // Refresh status so the meters update with whatever the server
        // currently thinks is blocking.
        await refreshCompletion();
        setCompleteError(
          "Server still sees some thresholds as unmet. Check the progress bars and try again.",
        );
      } else {
        const msg =
          err instanceof Error ? err.message : "Could not mark complete.";
        setCompleteError(msg);
      }
    } finally {
      setCompleting(false);
    }
  }, [state, navigate, refreshCompletion, moduleSlug]);

  if (state.kind === "loading") {
    return (
      <section
        className="mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-24"
        aria-busy="true"
      >
        <p className="text-sm text-muted-foreground" role="status">
          Loading lesson…
        </p>
      </section>
    );
  }

  if (state.kind === "locked") {
    return (
      <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-24">
        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
          Module locked
        </p>
        <h1 className="mt-2 text-2xl font-bold text-foreground">
          Pass the prior module's quiz to unlock this lesson
        </h1>
        <p className="mt-4 text-sm text-foreground">{state.blockingMessage}</p>
        <p className="mt-6 text-sm text-muted-foreground">
          <a
            className="font-semibold text-foreground underline"
            href="/course"
          >
            Back to course home
          </a>
        </p>
      </section>
    );
  }

  if (state.kind === "error") {
    return (
      <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-24">
        <h1 className="text-2xl font-bold text-foreground">
          {state.status === 404
            ? "Lesson not found"
            : state.status === 403
              ? "No active enrollment"
              : "Something went wrong"}
        </h1>
        <p className="mt-4 text-sm text-foreground" role="alert">
          {state.message}
        </p>
        <p className="mt-6 text-sm text-muted-foreground">
          <a
            className="font-semibold text-foreground underline"
            href="/course"
          >
            Back to course home
          </a>
        </p>
      </section>
    );
  }

  // state.kind === "ready"
  const { lesson, completion } = state;
  const readyToComplete =
    !completion.completed && completion.blocking_reasons.length === 0;

  // Lesson-walk nav. Always visible (top + bottom) so candidates can
  // step through a module sequentially or backtrack without bouncing
  // through course-home. Backend supplies prev/next slug paths in
  // ``completion`` so the link is correct even when the lesson is
  // already completed (the Mark Complete navigate-on-success path
  // doesn't fire for re-reads — this nav is the universal forward
  // mechanism).
  const PrevNextNav = (): React.JSX.Element => (
    <nav
      className="my-6 flex items-center justify-between gap-3 text-sm"
      aria-label="Lesson navigation"
    >
      {completion.prev_lesson_slug_path ? (
        <Link
          to={completion.prev_lesson_slug_path}
          className="inline-flex items-center gap-2 rounded-md border border-border bg-white px-3 py-2 font-semibold text-foreground hover:border-ca-purple/40 hover:bg-ca-gradient-subtle"
        >
          ← Previous lesson
        </Link>
      ) : (
        <Link
          to="/course"
          className="inline-flex items-center gap-2 rounded-md border border-border bg-white px-3 py-2 font-semibold text-foreground hover:border-ca-purple/40 hover:bg-ca-gradient-subtle"
        >
          ← Course home
        </Link>
      )}
      {(() => {
        const next = completion.next_lesson_slug_path;
        // If the next lesson is in a DIFFERENT module, the candidate
        // must pass this module's quiz first (DEC-0013 unlock gate),
        // so route them to the quiz instead of letting them dead-end
        // on the locked module page.
        const isSameModule =
          next !== null && next.startsWith(`/lesson/${moduleSlug}/`);
        const ctaClass =
          "inline-flex items-center gap-2 rounded-md bg-ca-gradient px-4 py-2 font-semibold text-white shadow-soft transition-all hover:-translate-y-px hover:shadow-card focus:outline-none focus:ring-2 focus:ring-ca-indigo focus:ring-offset-2";
        if (next !== null && isSameModule) {
          return (
            <Link to={next} className={ctaClass}>
              Next lesson →
            </Link>
          );
        }
        if (next !== null && !isSameModule) {
          return (
            <Link to={`/module/${moduleSlug}/quiz`} className={ctaClass}>
              Take Module {lesson.module_sequence} quiz →
            </Link>
          );
        }
        // End of course — point at the exam (final module's last lesson).
        return (
          <Link to="/exam/cait-foundations" className={ctaClass}>
            Take final exam →
          </Link>
        );
      })()}
    </nav>
  );

  return (
    <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-16">
      {/* Top breadcrumb nav — always visible so a user knows where they
       *  are in the module and can move sideways. */}
      <nav className="mb-6 text-xs text-muted-foreground">
        <Link to="/course" className="hover:text-foreground underline">
          Course home
        </Link>
        <span className="mx-2">/</span>
        <span>Module {lesson.module_sequence}</span>
        <span className="mx-2">/</span>
        <span>Lesson {lesson.sequence}</span>
      </nav>

      {/* Header */}
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-ca-indigo">
        Module {lesson.module_sequence} · Lesson {lesson.sequence}
      </p>
      <h1 className="mt-2 font-display text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
        {lesson.title}
      </h1>
      <p className="mt-3 text-sm text-muted-foreground">
        ~{lesson.estimated_minutes} min · {lesson.learning_objectives.length}{" "}
        learning {lesson.learning_objectives.length === 1 ? "objective" : "objectives"}
      </p>

      {/* Learning objectives */}
      {lesson.learning_objectives.length > 0 ? (
        <details className="mt-6 rounded-md border border-border bg-background p-4 text-sm">
          <summary className="cursor-pointer font-semibold text-foreground">
            Learning objectives
          </summary>
          <ul className="mt-3 list-disc space-y-2 pl-5 text-foreground">
            {lesson.learning_objectives.map((lo) => (
              <li key={lo.id}>
                <span className="font-mono text-xs uppercase text-muted-foreground">
                  {lo.id}
                </span>{" "}
                {lo.title}
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      {/* Video — Phase 0: only renders if video_url is set (DEC-0008).
       *  Captions track loads from ``video_captions_url`` (resolved
       *  server-side as ``lessons.captions_url`` || ``modules.intro_captions_url``).
       *  videoRef wires up the heartbeat hook to track playback (FR-C-2). */}
      {lesson.video_url !== null ? (
        // Branded video frame — 2px gradient ring with a 1px bg gap so
        // the gradient reads as a frame, not as edge bleed. Bumps the
        // perceived production value of the player without changing
        // any video behavior.
        <div className="mt-8 rounded-2xl bg-ca-gradient p-[2px] shadow-card">
          <div className="overflow-hidden rounded-[14px] bg-foreground">
            <video
              ref={videoRef}
              controls
              preload="metadata"
              src={lesson.video_url}
              className="block w-full"
            >
              {lesson.video_captions_url !== null ? (
                <track
                  kind="captions"
                  src={lesson.video_captions_url}
                  srcLang="en"
                  label="English"
                  default
                />
              ) : null}
            </video>
          </div>
        </div>
      ) : null}

      {/* Audio Study Guide — one per module, played below the video.
       *  Per DEC-0033 the player resumes against ``(user, study_guide)``
       *  state, so a learner pausing on Monday's lesson resumes on
       *  Tuesday's lesson within the same module at the same playhead.
       *  Renders nothing if no guide exists for this module (404). */}
      <StudyGuidePlayer moduleId={lesson.module_id} />

      {/* Article body — authored MDX bundled at frontend build time
       *  (DEC-0006 / Sprint 2 carry-over). The wrapping <div> is what
       *  the heartbeat hook tracks for scroll coverage. */}
      <div
        ref={articleRef}
        className="mt-8"
        data-testid="article-body"
      >
        <LessonBody articleMdxPath={lesson.article_mdx_path} />
      </div>

      {/* Progress meters */}
      <section
        className="mt-10 rounded-lg border border-border bg-white p-6"
        aria-label="Lesson progress"
      >
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Progress
        </h2>
        <dl className="mt-4 space-y-4 text-sm">
          <ProgressMeter
            label="Scroll coverage"
            valuePct={completion.scroll_pct}
            thresholdPct={completion.completion_threshold}
          />
          {completion.video_required ? (
            <ProgressMeter
              label="Video coverage"
              valuePct={completion.video_pct}
              thresholdPct={completion.completion_threshold}
            />
          ) : null}
          <div className="flex items-center justify-between">
            <dt className="font-medium text-foreground">Knowledge check</dt>
            <dd
              className={`text-xs font-semibold uppercase ${
                completion.kc_passed ? "text-emerald-700" : "text-muted-foreground"
              }`}
            >
              {completion.kc_passed
                ? `Passed${
                    completion.kc_latest_score !== null
                      ? ` · ${completion.kc_latest_score}%`
                      : ""
                  }`
                : "Not yet"}
            </dd>
          </div>
        </dl>
      </section>

      {/* Knowledge check */}
      {!completion.kc_passed && !completion.completed ? (
        <div className="mt-10">
          <KnowledgeCheck
            lessonId={lesson.id}
            onPassed={() => {
              void refreshCompletion();
            }}
            onFailedContinue={() => {
              void refreshCompletion();
            }}
          />
        </div>
      ) : null}

      {/* Complete button */}
      <div className="mt-10 flex items-center justify-between">
        <div>
          {completion.completed ? (
            <p className="text-sm font-semibold text-emerald-700">
              ✓ Lesson complete
            </p>
          ) : readyToComplete ? (
            <p className="text-sm text-foreground">
              All thresholds met — ready to mark complete.
            </p>
          ) : (
            <ul
              className={
                completion.blocking_reasons.length > 1
                  ? "ml-4 list-disc space-y-1 text-xs text-muted-foreground"
                  : "space-y-1 text-xs text-muted-foreground"
              }
            >
              {completion.blocking_reasons.map((reason) => (
                <li key={reason}>{humanizeBlockingReason(reason)}</li>
              ))}
            </ul>
          )}
          {completeError !== null ? (
            <p className="mt-2 text-sm text-red-700" role="alert">
              {completeError}
            </p>
          ) : null}
        </div>
        {!completion.completed ? (
          <button
            type="button"
            onClick={() => {
              void onMarkComplete();
            }}
            disabled={!readyToComplete || completing}
            className={[
              "inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold text-white shadow-soft transition-all",
              readyToComplete
                ? "bg-ca-gradient hover:-translate-y-px hover:shadow-card"
                : "bg-muted-foreground/50",
              "disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-ca-indigo focus:ring-offset-2",
            ].join(" ")}
          >
            {completing ? "Marking complete…" : "Mark complete ✓"}
          </button>
        ) : null}
      </div>

      {/* Bottom prev/next nav — same widget as the top, repeated here
       *  so a user finishing the lesson doesn't have to scroll back up
       *  to find the forward path. */}
      <PrevNextNav />
    </section>
  );
}

interface ProgressMeterProps {
  label: string;
  valuePct: number;
  thresholdPct: number;
}

function ProgressMeter({
  label,
  valuePct,
  thresholdPct,
}: ProgressMeterProps): React.JSX.Element {
  const met = valuePct >= thresholdPct;
  return (
    <div>
      <div className="flex items-center justify-between">
        <dt className="font-medium text-foreground">{label}</dt>
        <dd
          className={`text-xs font-semibold uppercase ${
            met ? "text-emerald-700" : "text-muted-foreground"
          }`}
        >
          {valuePct}% / {thresholdPct}%
        </dd>
      </div>
      <div
        className="mt-1 h-2 w-full overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-valuenow={valuePct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label}
      >
        <div
          className={`h-full transition-all ${met ? "bg-emerald-500" : "bg-ca-gradient"}`}
          style={{ width: `${Math.min(100, valuePct)}%` }}
        />
      </div>
    </div>
  );
}
